import React, { useState, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Search, ArrowUpDown, ArrowUp, ArrowDown, ChevronDown, ChevronRight, HelpCircle } from "lucide-react";
import { useTeacherStats, SortField, SortOrder } from "@/hooks/useTeacherStats";

import { StudentStatsFilters } from "./StudentStatsFilters";
import { TeacherStudentDetailsPanel } from "./TeacherStudentDetailsPanel";
import { format, parseISO, startOfMonth, endOfMonth } from "date-fns";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export const TeacherStatsTable: React.FC = () => {
  const now = new Date();
  const [sortField, setSortField] = useState<SortField>('total_students');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [dateMode, setDateMode] = useState<'month' | 'day'>('month');
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [dayStartDate, setDayStartDate] = useState<Date | undefined>(startOfMonth(now));
  const [dayEndDate, setDayEndDate] = useState<Date | undefined>(now);
  const [selectedTiers, setSelectedTiers] = useState<string[]>([]);
  const [selectedTeachers, setSelectedTeachers] = useState<string[]>([]);
  const [expandedTeacher, setExpandedTeacher] = useState<string | null>(null);

  // Compute actual startDate/endDate based on mode
  const startDate = useMemo(() => {
    if (dateMode === 'month') {
      return new Date(selectedYear, selectedMonth - 1, 1);
    }
    return dayStartDate;
  }, [dateMode, selectedMonth, selectedYear, dayStartDate]);

  const endDate = useMemo(() => {
    if (dateMode === 'month') {
      return endOfMonth(new Date(selectedYear, selectedMonth - 1, 1));
    }
    return dayEndDate;
  }, [dateMode, selectedMonth, selectedYear, dayEndDate]);

  // Fetch teacher list for filter dropdown
  const { data: teacherListData } = useQuery({
    queryKey: ['teacher-list-for-filter'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('v_teacher_stats')
        .select('teacher_code, nickname')
        .order('teacher_code', { ascending: true });
      if (error) throw error;
      return (data || []).map(t => ({ code: t.teacher_code, nickname: t.nickname }));
    },
    staleTime: 5 * 60 * 1000,
  });

  const teacherOptions = teacherListData || [];

  const { data, isLoading } = useTeacherStats({
    sortField,
    sortOrder,
    search,
    tiers: selectedTiers.length > 0 ? selectedTiers : undefined,
    teacherCodes: selectedTeachers.length > 0 ? selectedTeachers : undefined,
    startDate: startDate?.toISOString(),
    endDate: endDate?.toISOString(),
  });

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
  };

  const handleMonthChange = (month: number, year: number) => {
    setSelectedMonth(month);
    setSelectedYear(year);
  };

  const handleClear = () => {
    setDateMode('month');
    setSelectedMonth(now.getMonth() + 1);
    setSelectedYear(now.getFullYear());
    setDayStartDate(startOfMonth(now));
    setDayEndDate(now);
    setSelectedTiers([]);
    setSelectedTeachers([]);
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-4 w-4 ml-1 opacity-50" />;
    }
    return sortOrder === 'asc' 
      ? <ArrowUp className="h-4 w-4 ml-1" />
      : <ArrowDown className="h-4 w-4 ml-1" />;
  };

  const formatDateStr = (dateStr: string | null) => {
    if (!dateStr) return '-';
    try {
      return format(parseISO(dateStr), 'dd/MM/yy');
    } catch {
      return dateStr;
    }
  };

  const formatHours = (hours: number | null) => {
    if (hours === null || hours === undefined) return '-';
    return hours.toFixed(1);
  };

  const formatCount = (count: number | null) => {
    if (count === null || count === undefined) return '-';
    return count;
  };

  const hasDateFilter = !!(startDate || endDate);

  return (
    <div className="space-y-4">
      {/* Filters */}
      <StudentStatsFilters
        startDate={dayStartDate}
        endDate={dayEndDate}
        selectedTiers={selectedTiers}
        selectedTeachers={selectedTeachers}
        teacherOptions={teacherOptions}
        dateMode={dateMode}
        selectedMonth={selectedMonth}
        selectedYear={selectedYear}
        onStartDateChange={setDayStartDate}
        onEndDateChange={setDayEndDate}
        onTierChange={setSelectedTiers}
        onTeacherChange={setSelectedTeachers}
        onDateModeChange={setDateMode}
        onMonthChange={handleMonthChange}
        onClear={handleClear}
      />

      {/* Search */}
      <form onSubmit={handleSearch} className="flex gap-2 max-w-md">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="ค้นหาครู (รหัส, ชื่อ, ชื่อเล่น)..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-9"
          />
        </div>
        <button
          type="submit"
          className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
        >
          ค้นหา
        </button>
      </form>

      {/* Active Filter Indicator */}
      {(startDate || endDate) && (
        <div className="text-sm text-muted-foreground">
          กรองตามช่วงเวลา: {startDate ? format(startDate, 'dd/MM/yyyy') : 'ไม่กำหนด'} - {endDate ? format(endDate, 'dd/MM/yyyy') : 'ไม่กำหนด'}
        </div>
      )}

      {/* Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead 
                className="cursor-pointer hover:bg-muted/50 select-none"
                onClick={() => handleSort('teacher_code')}
              >
                <div className="flex items-center">
                  รหัสครู
                  {getSortIcon('teacher_code')}
                </div>
              </TableHead>
              <TableHead 
                className="cursor-pointer hover:bg-muted/50 select-none"
                onClick={() => handleSort('nickname')}
              >
                <div className="flex items-center">
                  ชื่อเล่น
                  {getSortIcon('nickname')}
                </div>
              </TableHead>
              <TableHead>ชื่อเต็ม</TableHead>
              <TableHead>Tier</TableHead>
              <TableHead 
                className="cursor-pointer hover:bg-muted/50 select-none text-right"
                onClick={() => handleSort('total_students')}
              >
                <div className="flex items-center justify-end">
                  นักเรียน
                  {getSortIcon('total_students')}
                </div>
              </TableHead>
              <TableHead 
                className="cursor-pointer hover:bg-muted/50 select-none text-right"
                onClick={() => handleSort('total_hours')}
              >
                <div className="flex items-center justify-end">
                  ชั่วโมงสอน
                  {getSortIcon('total_hours')}
                </div>
              </TableHead>
              <TableHead 
                className="cursor-pointer hover:bg-muted/50 select-none"
                onClick={() => handleSort('last_class_date')}
              >
                <div className="flex items-center">
                  สอนล่าสุด
                  {getSortIcon('last_class_date')}
                </div>
              </TableHead>
              {hasDateFilter && (
                <>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50 select-none text-right"
                    onClick={() => handleSort('depleted_students')}
                  >
                    <div className="flex items-center justify-end">
                      หมดคอร์ส
                      {getSortIcon('depleted_students')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50 select-none text-right"
                    onClick={() => handleSort('renewed_students')}
                  >
                    <div className="flex items-center justify-end">
                      ต่อคอร์ส
                      {getSortIcon('renewed_students')}
                    </div>
                  </TableHead>
                  <TableHead className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      % ต่อคอร์ส
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-xs">
                            <p className="font-medium mb-1">สูตรคำนวณ:</p>
                            <p className="text-sm">ต่อคอร์ส / หมดคอร์ส × 100</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </TableHead>
                </>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 10 }).map((_, index) => (
                <TableRow key={index}>
                  <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-12" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-8 ml-auto" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-12 ml-auto" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                  {hasDateFilter && (
                    <>
                      <TableCell><Skeleton className="h-4 w-8 ml-auto" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-8 ml-auto" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-12 ml-auto" /></TableCell>
                    </>
                  )}
                </TableRow>
              ))
            ) : data?.data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={hasDateFilter ? 10 : 7} className="text-center py-8 text-muted-foreground">
                  ไม่พบข้อมูลครู
                </TableCell>
              </TableRow>
            ) : (
              data?.data.map((teacher) => (
                <React.Fragment key={teacher.teacher_code}>
                  <TableRow 
                    className={`cursor-pointer transition-colors ${expandedTeacher === teacher.teacher_code ? 'bg-muted/50' : 'hover:bg-muted/30'}`}
                    onClick={() => setExpandedTeacher(
                      expandedTeacher === teacher.teacher_code ? null : teacher.teacher_code
                    )}
                  >
                    <TableCell className="font-mono text-sm">
                      <div className="flex items-center gap-2">
                        {hasDateFilter && (
                          expandedTeacher === teacher.teacher_code 
                            ? <ChevronDown className="h-4 w-4" />
                            : <ChevronRight className="h-4 w-4" />
                        )}
                        {teacher.teacher_code}
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">{teacher.nickname || '-'}</TableCell>
                    <TableCell>{teacher.full_name || '-'}</TableCell>
                    <TableCell>{teacher.tier || '-'}</TableCell>
                    <TableCell className="text-right font-medium">{teacher.total_students ?? 0}</TableCell>
                    <TableCell className="text-right">{formatHours(teacher.total_hours)}</TableCell>
                    <TableCell>{formatDateStr(teacher.last_class_date)}</TableCell>
                    {hasDateFilter && (
                      <>
                        <TableCell className="text-right">
                          <span className={teacher.depleted_students && teacher.depleted_students > 0 ? "text-red-600 font-medium" : ""}>
                            {formatCount(teacher.depleted_students)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className={teacher.renewed_students && teacher.renewed_students > 0 ? "text-green-600 font-medium" : ""}>
                            {formatCount(teacher.renewed_students)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {(() => {
                            const renewed = teacher.renewed_students || 0;
                            const depleted = teacher.depleted_students || 0;
                            return depleted > 0
                              ? `${Math.round((renewed / depleted) * 100)}%`
                              : '-';
                          })()}
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                  {hasDateFilter && expandedTeacher === teacher.teacher_code && (
                    <TableRow>
                      <TableCell colSpan={10} className="p-0">
                        <TeacherStudentDetailsPanel
                          teacherCode={teacher.teacher_code}
                          startDate={startDate}
                          endDate={endDate}
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </div>

    </div>
  );
};
